import { CircularProgress } from "@mui/material";
import React from "react";
import { ClientPortalEventsTable } from "./WidgetComponents/ClientPortalEventsTable";
import { RefreshContext } from "./WidgetLayout";
import { WidgetColumn } from "./Widget";

const HIDDEN_COLUMNS = new Set([
	"event_definition_id0",
	"event_form_header_id0",
	"form_header_id0",
	"people_id0",
	"units_id0",
	"group_profile_id0",
	"program_providing_service0",
	"program_responsible0",
	"site_providing_service0",
	"event_log_id0",
	"program_enrollment_event_id0",
	"service_track_event_id0",
	"is_people0",
	"events2do_id0",
	"events2do_id",
]);

const LISTED_COLUMN_CONFIG: Record<string, { caption: string; order: number; typeCode: string }> = {
	event_name0: { caption: "Event", order: 0, typeCode: "S" },
	due_date0: { caption: "Due Date", order: 1, typeCode: "DT" },
	event_name: { caption: "Event", order: 0, typeCode: "S" },
	due_date: { caption: "Due Date", order: 1, typeCode: "DT" },
};

function normalizeColumnKey(columnName: string): string {
	return columnName.replace(/0$/, "");
}

export function parseCustomReportResultSet(raw: unknown): { columns: WidgetColumn[]; data: Array<any> } {
	let rows = raw;
	if (typeof rows === "string") rows = JSON.parse(rows);
	if (!Array.isArray(rows) || rows.length === 0) return { columns: [], data: [] };

	const headerRow = rows[0] as Array<string>;
	const columns: WidgetColumn[] = headerRow.map((columnName, idx) => {
		const listed = LISTED_COLUMN_CONFIG[columnName] || LISTED_COLUMN_CONFIG[normalizeColumnKey(columnName)];
		return {
			Caption: listed?.caption ?? columnName.replace(/0$/, "").replace(/_/g, " "),
			ColumnName: columnName,
			ColumnOrder: listed?.order ?? idx,
			IsCustomColumn: false,
			IsGroupedColumn: false,
			IsListed: !!listed,
			TypeCode: listed?.typeCode ?? "S",
			IsPrimaryKey: false,
		};
	});

	const data = rows.slice(1).map((rowArr: Array<any>) => {
		const row: any = {};
		headerRow.forEach((columnName, idx) => {
			row[columnName] = rowArr[idx];
			if (columnName.endsWith("0")) {
				row[columnName.slice(0, -1)] = rowArr[idx];
			}
		});
		return row;
	});

	return { columns, data };
}

interface ClientPortalEventsWidgetProps {
	reportUrl?: string;
}

export default function ClientPortalEventsWidget(props: ClientPortalEventsWidgetProps) {
	const reportUrl = props.reportUrl ?? "report/GetClientPortalEvents";
	const setRefreshHandler = React.useContext(RefreshContext);
	const [config, setConfig] = React.useState([] as Array<WidgetColumn>);
	const [data, setData] = React.useState([] as Array<any>);
	const [load, setLoad] = React.useState(false);

	const getData = React.useCallback(() => {
		setLoad(true);
		fetch(reportUrl)
			.then((x) => {
				if (!x.ok) alert("An error occurred, please refresh the page.");
				return x.json();
			})
			.then((x) => {
				const parsed = parseCustomReportResultSet(x);
				setConfig(parsed.columns);
				setData(parsed.data);
				setLoad(false);
			})
			.catch(() => {
				setLoad(false);
			});
	}, [reportUrl]);

	React.useEffect(() => {
		getData();
	}, [getData]);

	React.useEffect(() => {
		if (setRefreshHandler) {
			setRefreshHandler(() => getData);
		}
		return () => {
			if (setRefreshHandler) {
				setRefreshHandler(null);
			}
		};
	}, [getData, setRefreshHandler]);

	return (
		<>
			{!load && <ClientPortalEventsTable Columns={config} Refresh={getData} Data={data} />}
			{load && (
				<div style={{ padding: "15px", alignItems: "center", flexDirection: "column", display: "flex" }}>
					<CircularProgress size={40}></CircularProgress>
				</div>
			)}
		</>
	);
}
